<script lang="ts">
	import { onMount, onDestroy } from "svelte";

	import { fly } from "svelte/transition";
	import { dldl, goto, yyyy, zzzz } from "./store.svelte";
	import { addOnsData } from "./lib/addOnsData";

	let ready: boolean = false;
	let selectedExtension: string | null = null;

	let selectExtension = (extension: any) => {
		if (selectedExtension === extension.id) {
			// Deselecting - go back to base learn-more
			selectedExtension = null;
			goto('/learn-more');
		} else {
			// Selecting a new extension
			selectedExtension = extension.id;
			goto(`/learn-more/${extension.urlSlug}`);
		}
	};

	let getSelectedExtension = () => {
		return addOnsData.find((ext) => ext.id === selectedExtension);
	};
	
	let getExtensionBySlug = (slug: string) => {
		return addOnsData.find((ext) => ext.urlSlug === slug);
	};

	onMount(() => {
		dldl("LearnMore.svelte", "LearnMore.svelte is mounted");
		ready = true;
		
		// Check if there's a selectedAddon in the store (from URL routing)
		if (yyyy.selectedAddon) {
			dldl("LearnMore.svelte", `Auto-expanding addon from URL: ${yyyy.selectedAddon}`);
			// Convert URL slug to extension ID
			const extension = getExtensionBySlug(yyyy.selectedAddon);
			if (extension) {
				selectedExtension = extension.id;
			}
		}
	});

	onDestroy(() => {
		yyyy.logo.time = 877.5;
		yyyy.logo.usage = "wait";
	});
</script>

{#if ready}
	<!-- <svelte:window on:pushstate={routing} /> -->
	<div class="column is-two-thirds not-too-wide">
		<div class="card lvwr-card" class:zeroBG={!ready}>
			<div
				class="card-content lvwr-card-content"
				transition:fly|local={{ y: -200 }}
			>
				{#if selectedExtension}
					{@const extension = getSelectedExtension()}
					{#if extension}
						<div class="expanded-extension-view">
							<button class="back-button" onclick={() => {
								selectedExtension = null;
								goto('/learn-more');
							}}>
								← Back
							</button>
							<h1 class="title thin lvwr-title" class:dim={!ready} class:bri={ready}>
								{extension.title}
							</h1>
							<div class="content lvwr-content" class:dim={!ready} class:bri={ready}>
								{#each extension.content as paragraph}
									<p>{paragraph}</p>
								{/each}
							</div>
						</div>
					{/if}
				{:else}
					<h1 class="title thin lvwr-title" class:dim={!ready} class:bri={ready}>
						What is Livewire
					</h1>
					<div class="content lvwr-content" class:dim={!ready} class:bri={ready}>
						<p>
							Livewire is a cutting-edge, cloud-based digital twin designed to empower
							distribution engineers with advanced tools to interrogate, model, and
							optimize electric distribution networks. Livewire enables engineers to
							interact with a virtual replica of their physical distribution network,
							allowing for analysis, simulation, and decision-making. This dynamic and
							interactive model provides engineers with unparalleled insights into
							network behavior and performance.
						</p>
						<p>
							Key capabilities include operating interrupting devices, running outage
							scenarios, and calculating reliability metrics. This allows utilities to
							identify issues proactively, optimize performance, and enhance system
							reliability and resilience.
						</p>
						<p>
							Livewire is available through an intuitive, easy-to-use web interface.
							There's no need to install software, provision hardware, or deal with
							cumbersome licensing dongles.
						</p>
						<p>
							Extend Livewire's core functionality with powerful add-ons designed for
							specific utility workflows:
						</p>
					</div>

					<!-- <h2 class="subtitle lvwr-subtitle">Extensions</h2> -->

					<div class="extensions-container">
						{#each addOnsData.filter((item) => item.id !== "livewire" && item.readyForAction !== false) as extension}
							<div
								class="extension-card-expandable"
								onclick={() => selectExtension(extension)}
							>
								<div class="extension-header">
									<div class="extension-info">
										<h3 class="extension-title">{extension.name}</h3>
										<p class="extension-description">{extension.description}</p>
									</div>
									<div class="extension-toggle">+</div>
								</div>
							</div>
						{/each}
					</div>

				{/if}
			</div>
			<footer
				class="card-footer"
				class:dim={!ready}
				class:bri={ready}
				transition:fly|local={{ y: 200 }}
			>
				<div class="card-footer-item sign-in-up-footer">
					<button
						onclick={() => {
							goto("/");
						}}
						class="button lvwr-toggle">Back</button
					>
					<button
						onclick={() => {
							goto("/schedule-a-demo");
						}}
						class="button lvwr-toggle">Schedule a Demo</button
					>
				</div>
			</footer>
		</div>
	</div>
{/if}

<style>
	.lvwr-card-content {
		max-height: 70vh;
		overflow-y: auto;
	}

	.extensions-container {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.extension-card-expandable {
		background-color: rgba(255, 255, 255, 0.05);
		border: 1px solid #e0e0e0;
		border-radius: 6px;
		border-left: 4px solid #ff3e00;
		transition: all 0.3s ease;
		cursor: pointer;
	}

	.extension-card-expandable:hover {
		background-color: rgba(255, 255, 255, 0.1);
		border-color: #ff3e00;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
		transform: translateY(-2px);
	}

	.extension-header {
		padding: 1rem;
		display: flex;
		justify-content: space-between;
		align-items: center;
		user-select: none;
	}

	.extension-info {
		flex: 1;
	}

	.extension-title {
		color: #ff3e00;
		font-size: 1.1rem;
		font-weight: 600;
		margin: 0 0 0.5rem 0;
	}

	.extension-description {
		color: #555;
		font-size: 0.9rem;
		line-height: 1.4;
		margin: 0;
	}

	.extension-toggle {
		font-size: 1.5rem;
		font-weight: bold;
		color: #ff3e00;
		margin-left: 1rem;
		min-width: 30px;
		text-align: center;
	}

	.expanded-extension-view {
		animation: fadeIn 0.3s ease;
	}

	.back-button {
		background: none;
		border: none;
		color: #ff3e00;
		cursor: pointer;
		font-size: 0.9rem;
		margin-bottom: 1rem;
		padding: 0.5rem 0;
		transition: all 0.2s ease;
	}

	.back-button:hover {
		color: #e63600;
		text-decoration: underline;
	}

	@keyframes fadeIn {
		from {
			opacity: 0;
			transform: translateY(10px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}
</style>
